import React, { useState } from 'react';
import { Star, CheckCircle2, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ratingsApi } from '../services/api';
import { toast } from 'sonner';

/**
 * RatingPrompt — inline 5-star widget used on the delivery receipt.
 * Consumer passes `orderId`, `target` ({ type, name }), and `alreadyRatedStars`
 * (from GET /ratings/order/:id — if present we render "You rated N ★" instead
 * of the input).
 */
const RatingPrompt = ({ orderId, target, alreadyRatedStars = null, alreadyComment = null, onRated }) => {
  const [stars, setStars] = useState(alreadyRatedStars || 0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState(alreadyComment || '');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(Boolean(alreadyRatedStars));

  if (done && stars) {
    return (
      <div className="rounded-xl bg-white border border-slate-200 p-4 flex items-center gap-3" data-testid={`rating-done-${target.type}`}>
        <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-none" />
        <div className="text-sm flex-1">
          <div className="font-semibold text-slate-900">
            You rated {target.name} {stars} <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400 inline -mt-0.5" />
          </div>
          {comment && <div className="text-slate-500 mt-0.5">&ldquo;{comment}&rdquo;</div>}
        </div>
      </div>
    );
  }

  const submit = async () => {
    if (!stars || busy) return;
    setBusy(true);
    try {
      await ratingsApi.create({ order_id: orderId, target_type: target.type, stars, comment });
      setDone(true);
      toast.success(`Thanks! Rated ${target.name} ${stars}★`);
      onRated?.(stars);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit rating');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid={`rating-prompt-${target.type}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-sm">
          <div className="font-semibold text-slate-900">Rate {target.name}</div>
          <div className="text-slate-500 text-xs">Your rating stays anonymous to the {target.type}.</div>
        </div>
      </div>
      <div className="flex items-center gap-1 mb-3" role="radiogroup" aria-label="Star rating">
        {[1, 2, 3, 4, 5].map((n) => {
          const active = (hover || stars) >= n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => setStars(n)}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              className="p-0.5"
              data-testid={`rating-star-${target.type}-${n}`}
              aria-label={`${n} star${n > 1 ? 's' : ''}`}
            >
              <Star className={`h-7 w-7 transition-transform ${active ? 'fill-amber-400 text-amber-400' : 'text-slate-300'} ${(hover || stars) === n ? 'scale-110' : ''}`} />
            </button>
          );
        })}
      </div>
      <Textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Optional feedback (max 500 chars)"
        maxLength={500}
        className="text-sm"
        data-testid={`rating-comment-${target.type}`}
      />
      <div className="mt-3 flex justify-end">
        <Button size="sm" onClick={submit} disabled={!stars || busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`rating-submit-${target.type}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Submit rating</>}
        </Button>
      </div>
    </div>
  );
};

export default RatingPrompt;
